import { useCallback } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { toast } from '@/lib/toast';

interface FieldError {
  field: string;
  message?: string;
}

function isFieldError(error: unknown): error is FieldError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'field' in error &&
    typeof (error as FieldError).field === 'string'
  );
}

interface UseEntityDialogSubmitOptions<T extends FieldValues> {
  form: UseFormReturn<T>;
  onSubmit: (values: T) => Promise<void>;
  onSuccess?: () => void;
  setRootError: (message: string) => void;
  fallbackErrorMessage?: string;
}

/**
 * Hook that wraps form submission with standardized error handling.
 *
 * Features:
 * - Catches field-specific errors and sets them on the form
 * - Shows toast notifications for all errors
 * - Handles both field-level and root-level errors consistently
 *
 * Usage:
 * ```tsx
 * const handleSubmit = useEntityDialogSubmit({
 *   form,
 *   onSubmit: async (values) => {
 *     await mutation.mutateAsync(values);
 *   },
 *   onSuccess: () => onOpenChange(false),
 *   setRootError,
 * });
 *
 * <form onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}>
 * ```
 */
export function useEntityDialogSubmit<T extends FieldValues>({
  form,
  onSubmit,
  onSuccess,
  setRootError,
  fallbackErrorMessage = "Impossibile salvare. Riprova o contatta l'assistenza.",
}: UseEntityDialogSubmitOptions<T>) {
  return useCallback(
    async (values: T) => {
      try {
        await onSubmit(values);
        onSuccess?.();
      } catch (error) {
        // Handle field-specific errors (e.g., DuplicateFieldError)
        if (isFieldError(error)) {
          const message = error instanceof Error ? error.message : 'Campo non valido.';
          form.setError(error.field as Path<T>, {
            type: 'manual',
            message,
          });
          toast.error(message);
          return;
        }

        // Handle root-level errors
        const errorMessage = error instanceof Error ? error.message : fallbackErrorMessage;
        setRootError(errorMessage);
        toast.error(errorMessage);
      }
    },
    [form, onSubmit, onSuccess, setRootError, fallbackErrorMessage]
  );
}
